export const initials = (name = "") =>
  name.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

/** Mutable date-format used by fmtDate / fmtDateTime. Populated from the
 * backend's `/settings/date-format` (public endpoint) on app boot by
 * `bootstrapDateFormat()`. Falls back to `DD-MM-YYYY` (India / UK / most of world). */
let _DATE_FORMAT = (typeof window !== "undefined" && localStorage.getItem("hc_date_format")) || "DD-MM-YYYY";

const _pad = (n) => String(n).padStart(2, "0");

const _pieces = (iso) => {
  // Accept full ISO ("2026-03-15T10:00:00Z"), plain ISO date ("2026-03-15"), or Date.
  const d = iso instanceof Date ? iso : new Date(iso);
  if (isNaN(d.getTime())) {
    // If it's already a YYYY-MM-DD string, split without timezone shifts.
    if (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const [y, m, dd] = iso.split("-");
      return { d: dd, m, y };
    }
    return null;
  }
  return { d: _pad(d.getDate()), m: _pad(d.getMonth() + 1), y: String(d.getFullYear()) };
};

const _formatPieces = (p, fmt) => {
  switch (fmt) {
    case "MM-DD-YYYY": return `${p.m}-${p.d}-${p.y}`;
    case "YYYY-MM-DD": return `${p.y}-${p.m}-${p.d}`;
    case "DD-MM-YYYY":
    default:           return `${p.d}-${p.m}-${p.y}`;
  }
};

export const setDateFormat = (fmt) => {
  if (!fmt) return;
  _DATE_FORMAT = fmt;
  try { localStorage.setItem("hc_date_format", fmt); } catch { /* ignore */ }
};

export const getDateFormat = () => _DATE_FORMAT;

/** Fetch the platform-wide date format from the backend and remember it.
 *  Called once at app boot from `App.js`. Non-blocking / silent-fail. */
export const bootstrapDateFormat = async (apiClient) => {
  try {
    const r = await apiClient.get("/settings/public");
    if (r?.data?.date_format) setDateFormat(r.data.date_format);
  } catch { /* keep existing / default */ }
};

export const fmtDate = (iso) => {
  if (!iso) return "";
  const p = _pieces(iso);
  if (!p) return String(iso);
  return _formatPieces(p, _DATE_FORMAT);
};

export const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = iso instanceof Date ? iso : new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    const p = { d: _pad(d.getDate()), m: _pad(d.getMonth() + 1), y: String(d.getFullYear()) };
    const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    return `${_formatPieces(p, _DATE_FORMAT)} · ${time}`;
  } catch { return String(iso); }
};

export const ageDisplay = (patient) => {
  if (!patient) return "";
  if (patient.age_type === "DOB") {
    try {
      const dob = new Date(patient.age_value);
      const now = new Date();
      let a = now.getFullYear() - dob.getFullYear();
      const m = now.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) a--;
      return `${a} Yrs`;
    } catch { return `${patient.age_value}`; }
  }
  return `${patient.age_value} Yrs`;
};

export const todayISO = () => new Date().toISOString().slice(0, 10);

export const timeSlots = (from = 7, to = 22, stepMin = 15) => {
  const out = [];
  for (let h = from; h <= to; h++) {
    for (let m = 0; m < 60; m += stepMin) {
      if (h === to && m > 0) break;
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
};

export const displayTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${String(hr).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
};

export const inr = (n) => `₹ ${Number(n || 0).toFixed(2)}`;
